/* BROOT — rendered art layer.
   Code-drawn pieces that stand in for photography: a lit bean form, volumetric
   smoke, a liquid crema surface, and a drifting grain field. Everything is 2D
   canvas shaded per-pixel at low resolution and scaled up, which reads soft and
   filmic.

   Usage:  <div class="bx-art" data-art="bean" data-ratio="0.8" data-res="240"></div>
           data-res is the internal render width (default 200, max 480). Raise it
           for larger pieces, leave it alone for cards.
   Kinds:  bean | smoke | crema | field

   19 Sep 2026 — budget. A 340x450 bean is 150,000 pixels, each one a
   handful of sqrt/exp/pow calls, and it was redrawn thirty times a second
   for as long as it was anywhere near the screen. On a phone that was a
   visible stutter through the whole "one lot at a time" band. Now:
     - phones and any device without a hover pointer get ONE still frame;
     - desktop animates at 12fps, and only while the piece is on screen;
     - reduced-motion also gets the still frame.
   The still frame is drawn with a small delay after boot so it never lands
   in the same task as first paint. */
(function () {
  if (window.__brootCanvas) return; window.__brootCanvas = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var still = reduce || window.innerWidth < 768 || !(window.matchMedia && window.matchMedia('(hover:hover) and (pointer:fine)').matches);
  var FRAME_MS = 83; /* ~12fps */
  var pieces = [];

  /* ---------- value noise ---------- */
  var P = new Uint8Array(512);
  (function () {
    var s = 1337 >>> 0;
    for (var i = 0; i < 256; i++) {
      s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
      P[i] = P[i + 256] = (s >>> 24) & 255;
    }
  })();
  function h2(x, y) { return P[(P[x & 255] + (y & 255)) & 255] / 255; }
  function sm(t) { return t * t * (3 - 2 * t); }
  function vnoise(x, y) {
    var xi = Math.floor(x), yi = Math.floor(y);
    var xf = x - xi, yf = y - yi;
    var u = sm(xf), v = sm(yf);
    var a = h2(xi, yi), b = h2(xi + 1, yi), c = h2(xi, yi + 1), d = h2(xi + 1, yi + 1);
    return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
  }
  function fbm(x, y, oct) {
    var t = 0, amp = 0.5, f = 1;
    for (var i = 0; i < oct; i++) { t += vnoise(x * f, y * f) * amp; amp *= 0.5; f *= 2; }
    return t;
  }

  /* ---------- shared shell ---------- */
  function makePiece(host) {
    var kind = host.getAttribute('data-art') || 'bean';
    var ratio = parseFloat(host.getAttribute('data-ratio')) || 1;
    var c = document.createElement('canvas');
    c.className = 'bx-art__c';
    c.setAttribute('aria-hidden', 'true');
    host.appendChild(c);
    var ctx = c.getContext('2d', { alpha: true });
    var p = { host: host, kind: kind, ratio: ratio, c: c, ctx: ctx, w: 0, h: 0, t: 0, on: false, img: null, parts: null };
    size(p);
    return p;
  }

  function size(p) {
    var r = p.host.getBoundingClientRect();
    if (!r.width) return;
    var base = parseInt(p.host.getAttribute('data-res'), 10) || (p.kind === 'field' ? 300 : 200);
    if (base > 480) base = 480;
    /* a still frame needs less resolution than a moving one to read as soft */
    if (still && base > 300) base = 300;
    var w = base, h = Math.round(base * (r.height / r.width || p.ratio));
    if (!h || h < 40) h = Math.round(base * p.ratio);
    if (h > 720) h = 720;
    if (p.w === w && p.h === h) return;
    p.w = w; p.h = h;
    p.c.width = w; p.c.height = h;
    p.fade = null;
    p.img = p.ctx.createImageData(w, h);
    p.parts = null;
  }

  /* ---------- 1. BEAN — lit ellipsoid with a rotating crease ---------- */
  function drawBean(p, t) {
    var w = p.w, h = p.h, d = p.img.data;
    var a = 0.62, b = 0.86;
    var lx = -0.42, ly = -0.62, lz = 0.66;
    var rot = t * 0.16;
    var cr = Math.cos(rot), sr = Math.sin(rot);
    var i = 0;
    for (var py = 0; py < h; py++) {
      var v = (py / h) * 2 - 1;
      for (var pxi = 0; pxi < w; pxi++) {
        var u = (pxi / w) * 2 - 1;
        var q = 1 - (u * u) / (a * a) - (v * v) / (b * b);
        if (q <= 0) { d[i] = 0; d[i + 1] = 0; d[i + 2] = 0; d[i + 3] = 0; i += 4; continue; }
        var z = Math.sqrt(q) * 0.52;
        var nx = u / (a * a), ny = v / (b * b), nz = z / (0.52 * 0.52);
        var nl = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
        nx /= nl; ny /= nl; nz /= nl;
        var xo = u * cr + z * sr;
        var g = Math.exp(-(xo * xo) / 0.006);
        var wob = 0.16 * Math.sin(v * 3.1 + 0.6);
        var g2 = Math.exp(-((xo - wob) * (xo - wob)) / 0.010);
        var groove = Math.max(g, g2 * 0.9);
        nx += groove * (xo > 0 ? 0.85 : -0.85);
        var nl2 = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
        nx /= nl2; ny /= nl2; nz /= nl2;
        var lam = Math.max(0, nx * lx + ny * ly + nz * lz);
        var rim = Math.pow(1 - Math.max(0, nz), 4.2) * 0.30;
        var spec = Math.pow(lam, 42) * 0.62 * (1 - groove);
        var occ = 1 - groove * 0.72;
        var tone = (0.030 + Math.pow(lam, 1.25) * 0.86) * occ + rim + spec;
        tone *= 0.955 + fbm(pxi * 0.14, py * 0.14 + t * 0.05, 2) * 0.09;
        if (tone < 0) tone = 0; if (tone > 1) tone = 1;
        var edge = Math.min(1, q * 9);
        d[i] = (244 * tone) | 0;
        d[i + 1] = (238 * tone) | 0;
        d[i + 2] = (228 * tone) | 0;
        d[i + 3] = (255 * edge) | 0;
        i += 4;
      }
    }
    p.ctx.putImageData(p.img, 0, 0);
  }

  /* ---------- 2. SMOKE — volumetric drift ---------- */
  function drawSmoke(p, t) {
    var w = p.w, h = p.h, d = p.img.data, i = 0;
    for (var py = 0; py < h; py++) {
      var fy = py / h;
      for (var pxi = 0; pxi < w; pxi++) {
        var fx = pxi / w;
        var wx = fbm(fx * 1.7 + 4.2, fy * 1.1 + t * 0.045, 3);
        var wy = fbm(fx * 1.9 - 2.6, fy * 1.3 + t * 0.055, 3);
        var n = fbm(fx * 2.6 + wx * 1.5, fy * 1.5 + t * 0.13 + wy * 1.5, 4);
        var v = n;
        var col = Math.exp(-Math.pow((fx - 0.5) * 2.35, 2));
        var rise = Math.pow(fy, 0.85);
        var amt = Math.max(0, v - 0.40) * 3.0 * col * rise;
        amt *= Math.min(1, fx * 4.2) * Math.min(1, (1 - fx) * 4.2) * Math.min(1, fy * 2.4) * Math.min(1, (1 - fy) * 5.0);
        if (amt > 1) amt = 1;
        amt = amt * amt * (3 - 2 * amt);
        var tone = 0.62 + v * 0.42;
        if (tone > 1) tone = 1;
        d[i] = (244 * tone) | 0;
        d[i + 1] = (239 * tone) | 0;
        d[i + 2] = (230 * tone) | 0;
        d[i + 3] = (amt * 138) | 0;
        i += 4;
      }
    }
    p.ctx.putImageData(p.img, 0, 0);
  }

  /* ---------- 3. CREMA — liquid surface, very slow drift ---------- */
  function drawCrema(p, t) {
    var w = p.w, h = p.h, d = p.img.data, i = 0;
    for (var py = 0; py < h; py++) {
      var fy = py / h;
      for (var pxi = 0; pxi < w; pxi++) {
        var fx = pxi / w;
        var dx = (fx - 0.5) * 2, dy = (fy - 0.5) * 2;
        var r = Math.sqrt(dx * dx + dy * dy);
        if (r > 1) { d[i] = 0; d[i + 1] = 0; d[i + 2] = 0; d[i + 3] = 0; i += 4; continue; }
        var dome = Math.sqrt(Math.max(0, 1 - r * r));
        var dr = t * 0.018;
        var cell = fbm(fx * 15 + dr, fy * 15 - dr * 0.6, 3);
        var fine = fbm(fx * 38 - dr * 0.4, fy * 38 + dr, 2);
        var tex = cell * 0.72 + fine * 0.28;
        var sx = fx - 0.36, sy = fy - 0.30;
        var sheen = Math.exp(-(sx * sx + sy * sy) / 0.075);
        var lam = 0.34 + dome * 0.42;
        var tone = (0.07 + tex * 0.34) * lam + sheen * 0.38 * dome;
        tone *= 0.35 + 0.65 * Math.pow(dome, 0.55);
        if (tone < 0) tone = 0; if (tone > 1) tone = 1;
        var edge = Math.min(1, (1 - r) * 16);
        d[i] = (244 * tone) | 0;
        d[i + 1] = (237 * tone) | 0;
        d[i + 2] = (226 * tone) | 0;
        d[i + 3] = (255 * edge) | 0;
        i += 4;
      }
    }
    p.ctx.putImageData(p.img, 0, 0);
  }

  /* ---------- 4. FIELD — dust motes ---------- */
  function drawField(p, t) {
    var w = p.w, h = p.h, ctx = p.ctx;
    if (!p.parts) {
      var n = Math.round((w * h) / 620);
      if (n > 560) n = 560;
      p.parts = [];
      for (var k = 0; k < n; k++) {
        p.parts.push({
          x: h2(k, 7) * w, y: h2(k + 40, 19) * h,
          s: 0.30 + h2(k + 90, 3) * 1.05,
          v: 0.10 + h2(k + 140, 11) * 0.42,
          o: 0.10 + h2(k + 200, 5) * 0.40
        });
      }
    }
    var vel = 0;
    ctx.clearRect(0, 0, w, h);
    for (var j = 0; j < p.parts.length; j++) {
      var q = p.parts[j];
      q.y -= q.v * (0.5 + vel * 4.5);
      if (q.y < -2) q.y = h + 2;
      var sway = Math.sin(t * 0.4 + q.x * 0.05) * (0.6 + vel * 2);
      ctx.globalAlpha = q.o * (0.7 + vel * 0.3);
      ctx.fillStyle = '#F4EFE6';
      ctx.beginPath();
      ctx.arc(q.x + sway, q.y, q.s, 0, 6.283);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    if (!p.fade) {
      var g = ctx.createRadialGradient(w * 0.5, h * 0.42, 0, w * 0.5, h * 0.42, Math.max(w, h) * 0.62);
      g.addColorStop(0, 'rgba(0,0,0,1)');
      g.addColorStop(0.55, 'rgba(0,0,0,0.85)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      p.fade = g;
    }
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fillStyle = p.fade;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'source-over';
  }

  var DRAW = { bean: drawBean, smoke: drawSmoke, crema: drawCrema, field: drawField };

  /* ---------- loop (desktop only) ---------- */
  var last = 0, running = false;
  function loop(now) {
    running = false;
    var any = false;
    if (now - last > FRAME_MS) {
      last = now;
      var t = now / 1000;
      for (var i = 0; i < pieces.length; i++) {
        var p = pieces[i];
        if (!p.on || !p.w) continue;
        any = true;
        (DRAW[p.kind] || drawBean)(p, t);
      }
    } else { any = true; }
    if (any && !still && !document.hidden) { running = true; requestAnimationFrame(loop); }
  }
  function kick() { if (!running && !still) { running = true; requestAnimationFrame(loop); } }

  var t0 = 2.3;
  function drawStill(p) { if (p.w) (DRAW[p.kind] || drawBean)(p, t0); }

  function init() {
    var hosts = document.querySelectorAll('.bx-art[data-art]');
    if (!hosts.length) return;
    Array.prototype.forEach.call(hosts, function (hst) {
      if (hst.__art) return; hst.__art = 1;
      pieces.push(makePiece(hst));
    });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (en) {
        en.forEach(function (x) {
          for (var i = 0; i < pieces.length; i++) {
            if (pieces[i].host === x.target) {
              pieces[i].on = x.isIntersecting;
              /* the still frame is drawn the first time the piece comes near */
              if (x.isIntersecting && !pieces[i].drawn) { pieces[i].drawn = true; drawStill(pieces[i]); }
            }
          }
        });
        kick();
      }, { rootMargin: '25% 0px' });
      pieces.forEach(function (p) { io.observe(p.host); });
    } else {
      pieces.forEach(function (p) { p.on = true; p.drawn = true; drawStill(p); });
      kick();
    }

    window.addEventListener('resize', function () {
      pieces.forEach(function (p) { size(p); if (p.drawn) drawStill(p); });
      kick();
    }, { passive: true });
    if (!still) {
      window.addEventListener('scroll', kick, { passive: true });
      document.addEventListener('visibilitychange', function () { if (!document.hidden) kick(); });
    }
  }

  function boot() { setTimeout(init, 400); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', init);
})();
